import type { AccessibilityFocusRef, UniversalFocusOptions } from './types';

export type { UniversalFocusOptions } from './types';

function hasFocusMethod(value: unknown): value is { focus: () => void } {
    return value != null && typeof value === 'object' && 'focus' in value && typeof value.focus === 'function';
}

/**
 * Sets focus on the referenced element using the DOM focus() API.
 *
 * @returns `true` if the focus request was dispatched, `false` if the ref was empty.
 */
export function setAccessibilityFocus(ref: AccessibilityFocusRef): boolean {
    const element: unknown = ref.current;
    if (hasFocusMethod(element)) {
        element.focus();
        return true;
    }
    return false;
}

/**
 * Sets focus on the referenced element using the DOM focus() API.
 *
 * The `keyboardFocus` option is a no-op on web (DOM focus already covers
 * both screen-reader and keyboard focus).
 *
 * When `ensureFocusable: true`, plain `HTMLElement` targets without an
 * explicit `tabindex` get `tabindex="-1"` set before `.focus()` so that
 * non-focusable containers (e.g. a `<Box>` → `<div>`) actually receive
 * focus instead of silently no-op'ing. Use this for "move focus to a
 * newly revealed region" patterns (RN-3877).
 *
 * @returns `true` if the focus request was dispatched, `false` if the ref was empty.
 */
export function setUniversalFocus(
    ref: AccessibilityFocusRef,
    options: UniversalFocusOptions = {},
): boolean {
    const { ensureFocusable = false } = options;
    const element: unknown = ref.current;
    if (!hasFocusMethod(element)) {
        return false;
    }
    if (
        ensureFocusable &&
        typeof HTMLElement !== 'undefined' &&
        element instanceof HTMLElement &&
        !element.hasAttribute('tabindex')
    ) {
        element.setAttribute('tabindex', '-1');
    }
    element.focus();
    return true;
}
